import { Alert, Box, Button, Link, Stack, Typography } from "@mui/material"
import { FC } from "react"
import { useNavigate } from "react-router"
import { ReservationSyncDto } from "src/features/things/reservationSyncApi"
import { CountedNeverDrift } from "./CountedNeverDrift"
import { PresenceMatrix } from "./PresenceMatrix"
import { fmtCount, PartLoader } from "./ReservationSyncChrome"
import { useReservationSyncReport } from "./useReservationSyncReport"

type DriftRowDto = {
  sstId: string
  driftClass: string
  presence?: Record<"v1" | "v2" | "ddb" | "olap", boolean>
  nonDriftFlags?: string[]
}

/**
 * The run page's Overview tab (LOCKER-421 `DoD8`).
 *
 * Two columns, and the split is the argument: **what diverged** on the left, and
 * **what is broken but not divergence** on the right. Today's page has only the
 * first, so a locker whose numbers are dominated by 1.6% poisoned expiries reads
 * as a locker with a lot of drift.
 *
 * The presence matrix needs the stored drift rows, which load part by part — a
 * locker holds thousands of rows and the report is split over 6 MB objects. So
 * this tab loads on demand like every other report surface rather than blocking
 * the page, and the loader says how much of the matrix is in front of you.
 */
export const OverviewPanel: FC<{
  run: ReservationSyncDto
  /** Other lockers in this tenant, for the context list at the bottom. */
  siblings: ReservationSyncDto[]
}> = ({ run, siblings }) => {
  const navigate = useNavigate()
  const { reportOf, loadNextPart } = useReservationSyncReport<DriftRowDto>(run)
  const report = reportOf("drift")
  const suppressed = run.driftTotal === null

  return (
    <Box data-ui="UI-SL2W-162"
      sx={{
        display: "grid",
        gridTemplateColumns: { xs: "1fr", lg: "minmax(0, 5fr) minmax(0, 6fr)" },
        gap: 3,
        alignItems: "start",
      }}
    >
      <Box sx={{ border: 1, borderColor: "divider", borderRadius: 1, p: 2.5 }}>
        <Typography sx={{ fontSize: "0.88rem", fontWeight: 600 }}>
          Four-way presence — this locker
        </Typography>

        {!suppressed && (
          <>
            <PartLoader
              report={report}
              onLoadMore={() => loadNextPart("drift")}
              label="Drift report"
            />
            {!report.partsLoaded && !report.loading && (
              <Button
                size="small"
                variant="outlined"
                sx={{ mt: 1, mb: 2 }}
                onClick={() => loadNextPart("drift")}
              >
                Load the drift report
              </Button>
            )}
          </>
        )}

        <PresenceMatrix rows={report.rows} suppressed={suppressed} />

        {!suppressed && report.partsLoaded > 0 && (
          <Alert severity="info" variant="outlined" sx={{ mt: 2 }}>
            <Typography variant="body2">
              <strong>
                {fmtCount(run.driftTotal)} row{run.driftTotal === 1 ? "" : "s"} a
                sync would act on.
              </strong>{" "}
              Every other reservation on this locker agrees across the sources that
              hold it. The counts beside each class are on the Drift tab.
            </Typography>
          </Alert>
        )}

        {siblings.length > 0 && (
          <Box sx={{ mt: 3, pt: 2, borderTop: 1, borderColor: "divider" }}>
            <Typography
              variant="caption"
              sx={{ fontWeight: 700, letterSpacing: "0.07em", color: "text.disabled" }}
            >
              OTHER LOCKERS IN {(run.tenantName ?? run.tenantId).toUpperCase()}
            </Typography>
            <Stack spacing={1} sx={{ mt: 1 }}>
              {siblings.slice(0, 5).map((sibling) => (
                <Link
                  key={sibling.runId}
                  component="button"
                  type="button"
                  onClick={() =>
                    navigate(
                      `/sa/reservation-syncs/${encodeURIComponent(sibling.runId)}?tenantId=${encodeURIComponent(sibling.tenantId)}`,
                    )
                  }
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.25,
                    border: 1,
                    borderColor: "divider",
                    borderRadius: 1,
                    p: 1.25,
                    textAlign: "left",
                    textDecoration: "none",
                  }}
                >
                  <Typography variant="caption" sx={{ fontWeight: 700 }}>
                    SST {sibling.sstApiVersion.toUpperCase()}
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {sibling.lockerName ?? sibling.sstLockerId}
                  </Typography>
                  <Box sx={{ flexGrow: 1 }} />
                  <Typography variant="caption" sx={{ color: "text.secondary" }}>
                    drift {fmtCount(sibling.driftTotal)}
                  </Typography>
                </Link>
              ))}
            </Stack>
            <Typography
              variant="caption"
              sx={{ display: "block", mt: 1, color: "text.disabled", lineHeight: 1.5 }}
            >
              Context only. Each locker is assessed, synced and stored on its own —
              there is no tenant-level verdict, and no number here is summed across
              lockers.
            </Typography>
          </Box>
        )}
      </Box>

      <Box sx={{ border: 1, borderColor: "divider", borderRadius: 1, p: 2.5 }}>
        <CountedNeverDrift run={run} />
        <Stack
          direction="row"
          spacing={1.5}
          alignItems="center"
          sx={{ mt: 3, pt: 2, borderTop: 1, borderColor: "divider" }}
        >
          <Typography variant="caption" sx={{ color: "text.secondary" }}>
            Read-only. Nothing in this console triggers, confirms, applies or reverts
            a sync — both modes stay locally operated from a shell.
          </Typography>
        </Stack>
      </Box>
    </Box>
  )
}
